import React from 'react'
import { Link } from 'gatsby'
import { kebabCase } from 'lodash'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Button } from '@material-ui/core'
import HeaderImage from './HeaderImage.js'

const PromoBanner = styled.div`
  position: relative;
  height: 0;
  width: 100%;
  padding-top: 26%;
  text-align: left;
  background: ${props => props.theme.palette.secondary.main};
  background-image: linear-gradient(
    135deg,
    ${props => props.theme.palette.primary.main} 0%,
    ${props => props.theme.palette.secondary.main} 100%
  );
  overflow: hidden;
  @media (max-width: 1700px) {
    padding-top: 30%;
  }
  @media (max-width: 1440px) {
    padding-top: 37.5%;
  }
  @media (max-width: 768px) {
    padding-top: 45%;
  }
`
const PromoContent = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 50%;
  padding: ${props => props.theme.spacing(12)}px
    ${props => props.theme.spacing(4)}px ${props => props.theme.spacing(4)}px
    ${props => props.theme.spacing(12)}px;
  z-index: 9;
  @media (max-width: 1900px) {
    width: 60%;
    padding: ${props => props.theme.spacing(8)}px
      ${props => props.theme.spacing(4)}px ${props => props.theme.spacing(4)}px
      ${props => props.theme.spacing(6)}px;
  }
  @media (max-width: 1700px) {
    width: 80%;
    padding: ${props => props.theme.spacing(4)}px
      ${props => props.theme.spacing(4)}px ${props => props.theme.spacing(4)}px
      ${props => props.theme.spacing(6)}px;
  }
  @media (max-width: 1440px) {
    width: 80%;
  }
  @media (max-width: 768px) {
    width: 100%;
    padding: ${props => props.theme.spacing(4)}px
      ${props => props.theme.spacing(6)}px;
  }
`
const PromoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 8;
  opacity: 0.4;
  background-image: linear-gradient(
    135deg,
    ${props => props.theme.palette.primary.main} 0%,
    ${props => props.theme.palette.secondary.main} 100%
  );
`

const PromoHeader = styled.h2`
  margin: ${props => props.theme.spacing(10)}px
    ${props => props.theme.spacing(0)}px ${props => props.theme.spacing(1)}px
    ${props => props.theme.spacing(0)}px;
  font-weight: 900;
  font-size: 4.5rem;
  line-height: 3.25rem;
  text-transform: uppercase;
  font-family: 'Work Sans', sans-serif;
  color: ${props => props.theme.palette.common.white};
  @media (max-width: 1700px) {
    margin: ${props => props.theme.spacing(6)}px 0
      ${props => props.theme.spacing(1)}px 0;
    font-size: 4.125rem;
    line-height: 3rem;
  }
  @media (max-width: 1440px) {
    margin: ${props => props.theme.spacing(6)}px 0
      ${props => props.theme.spacing(1)}px 0;
    font-size: 3.725rem;
    line-height: 3rem;
  }
  @media (max-width: 738px) {
    margin: ${props => props.theme.spacing(2)}px 0
      ${props => props.theme.spacing(1)}px 0;
    font-weight: 900;
    font-size: 2.5rem;
    line-height: 2rem;
  }
  @media (max-width: 330px) {
    margin: ${props => props.theme.spacing(2)}px 0
      ${props => props.theme.spacing(1)}px 0;
    font-weight: 900;
    font-size: 2rem;
    line-height: 1.5rem;
  }
`

const PromoCopy = styled.p`
  margin: 0 0 ${props => props.theme.spacing(6)}px 0;
  font-size: 1.5rem;
  line-height: 1.5rem;
  text-transform: uppercase;
  color: ${props => props.theme.palette.common.white};
  @media (max-width: 1440px) {
    font-size: 1.25rem;
    line-height: 1.25rem;
    margin: 0 0 ${props => props.theme.spacing(4)}px 0;
  }
  @media (max-width: 738px) {
    margin-bottom: 0;
    font-size: 1.125rem;
    line-height: 1.125rem;
  }
`

const PromoButtonBase = styled(Button)`
  height: 48px;
  padding: ${props => props.theme.spacing(1)}px
    ${props => props.theme.spacing(4)}px;
  border-radius: 4px;
  color: ${props => props.theme.palette.text};
  text-align: center;
  text-transform: uppercase;
  font-weight: bold;
  font-size: 1.25rem;
  line-height: 1.5rem;
  cursor: pointer;
  box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.2);
  @media (max-width: 1700px) {
    height: 40px;
    margin: ${props => props.theme.spacing(1)}px 0;
  }
  @media (max-width: 768px) {
    height: 40px;
    padding: ${props => props.theme.spacing(0.5)}px
      ${props => props.theme.spacing(4)}px;
    margin: ${props => props.theme.spacing(2)}px 0;
    font-size: 1rem;
    line-height: 1.5rem;
  }
  @media (max-width: 330px) {
    height: 36px;
  }
`

const MarketingHeader = props => {
  const { promoHeader, promoCopy, promoButton, sport } = props
  const promoURL = `/${kebabCase(sport)}`

  return (
    <PromoBanner>
      <PromoContent>
        <PromoHeader>{promoHeader}</PromoHeader>
        <PromoCopy>{promoCopy}</PromoCopy>
        <PromoButtonBase
          variant="contained"
          color="secondary"
          component={Link}
          to={promoURL}
        >
          {promoButton}
        </PromoButtonBase>
      </PromoContent>
      <PromoOverlay />
      <HeaderImage sport={sport} />
    </PromoBanner>
  )
}

MarketingHeader.propTypes = {
  promoHeader: PropTypes.string.isRequired,
  promoCopy: PropTypes.string.isRequired,
  promoButton: PropTypes.string.isRequired,
  sport: PropTypes.any,
}

export default MarketingHeader
